import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { getMiningStatus, rebootMiningRig, collectMiningCoins, syncUserData, getUserProfile } from '../services/api';
import './Dashboard.css';

// Constants for mining - must match server values
// New calculation: 25 base H/s + (parts * 5 H/s), 75 H/s = 0.000400 coins/sec
// Rate per H/s = 0.000400 / 75 = 0.00000533333 coins/sec per H/s
const BASE_HASH_RATE = 25; // Base hash rate in H/s
const HASH_RATE_PER_PART = 5; // Each part adds 5 H/s
const COINS_PER_HASH_RATE = 0.00000533333; // Coins per second per H/s (0.000400 / 75)
// Remove caps on mining time and rewards to allow unlimited accumulation
// const MAX_MINING_TIME = 600; // Maximum time in seconds (10 minutes)
// const MAX_PENDING_REWARDS = calculating dynamically based on parts

// Session storage keys for collection tracking
const LAST_COLLECTION_KEY = 'mining_last_collection';
const PENDING_REWARDS_KEY = 'mining_pending_rewards';
const COLLECTION_AMOUNT_KEY = 'mining_last_collection_amount';

const Dashboard = () => {
  const { user, updateUser } = useAuth();
  const { notifyMiningReward } = useNotifications();
  const navigate = useNavigate();
  const [miningStatus, setMiningStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collectingRewards, setCollectingRewards] = useState(false);
  const [rebootingRig, setRebootingRig] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [clientTime, setClientTime] = useState(Date.now());
  
  // Use ref to store the latest fetchMiningStatus function to avoid stale closures
  const fetchMiningStatusRef = useRef();
  
  // Helper to format numbers with max 3 decimal places
  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0.000";
    // Ensure we're working with a number
    const numValue = parseFloat(num);
    if (isNaN(numValue)) return "0.000";
    // Format with max 3 decimal places
    return numValue.toFixed(3);
  };

  
  // Record collection in session storage
  const recordCollection = (amount) => {
    try {
      const now = Date.now();
      // Store the timestamp to prevent rapid re-collection
      sessionStorage.setItem(LAST_COLLECTION_KEY, now.toString());
      sessionStorage.setItem(PENDING_REWARDS_KEY, '0');
      sessionStorage.setItem(COLLECTION_AMOUNT_KEY, amount.toString());
      
      console.log(`Collection of ${amount} WR recorded in session storage at ${new Date(now).toISOString()}`);
    } catch (e) {
      console.error('Error recording collection in storage:', e);
    }
  };
  
  // Check if collection happened recently
  const hasRecentCollection = () => {
    try {
      const lastCollectionStr = sessionStorage.getItem(LAST_COLLECTION_KEY);
      if (!lastCollectionStr) return false;
      
      const lastCollection = parseInt(lastCollectionStr);
      const now = Date.now();
      const timeSinceCollection = now - lastCollection;
      
      // Increase timeout to 60 seconds to prevent the UI from showing rewards too quickly
      // This gives the server more time to process the update and respond
      return timeSinceCollection < 60000; // 60 seconds
    } catch (e) {
      console.error('Error checking for recent collection:', e);
      return false;
    }
  };
  
  // Function to fetch mining status
  const fetchMiningStatus = useCallback(async () => {
    try {
      // Check if status refresh is temporarily disabled after a reboot
      const disableRefreshUntil = sessionStorage.getItem('disable_mining_refresh');
      if (disableRefreshUntil) {
        const disableTime = parseInt(disableRefreshUntil);
        if (Date.now() < disableTime) {
          console.log('Mining status refresh is temporarily disabled after reboot');
          
          // Just update the timestamp to keep UI responsive
          setMiningStatus(prevStatus => {
            if (!prevStatus) return null;
            return { 
              ...prevStatus,
              lastUpdateTime: Date.now() 
            };
          });
          return;
        } else {
          // Clear the disable flag once time has passed
          sessionStorage.removeItem('disable_mining_refresh');
        }
      }
      
      // Default to active unless mining is disabled
      let currentStoredStatus = miningStatus ? {
        isActive: !miningStatus.miningDisabled,
        status: miningStatus.miningDisabled ? 'Locked - Tasks Complete' : 'Running',
        miningDisabled: miningStatus.miningDisabled,
        lastCollection: miningStatus.lastCollection
      } : {
        isActive: true,
        status: 'Running',
        miningDisabled: false
      };
      
      // Check for recent collection first
      const recentCollection = hasRecentCollection();
      
      if (recentCollection) {
        console.log('Recent collection detected, maintaining zero balance');
        
        // Update mining status to force 0 balance but maintain server status
        setMiningStatus(prevStatus => {
          if (!prevStatus) {
            // If no previous status on refresh, we need to get totalMined/miningCap from server
            // Don't skip the API call in this case
            return null;
          }
          
          return {
            ...prevStatus,
            isActive: currentStoredStatus.isActive,
            status: currentStoredStatus.status,
            miningDisabled: currentStoredStatus.miningDisabled,
            pendingRewards: 0,
            lastUpdateTime: Date.now(),
            // IMPORTANT: Preserve totalMined and miningCap data
            totalMined: prevStatus.totalMined,
            miningCap: prevStatus.miningCap,
            capReached: prevStatus.capReached
          };
        });
        
        // Only skip the API call if we have previous status with totalMined data
        if (miningStatus && miningStatus.totalMined !== undefined) {
          return;
        }
        // Otherwise, proceed with API call to get totalMined data
      }
      
      // No recent collection, proceed with fetching status from server
      const miningData = await getMiningStatus();
      
      if (miningData) {
        // Calculate mining rewards - now completely based on server-side timestamps
        const now = Date.now();
        const lastCollection = miningData.lastCollection || (now - 3600000);
        const secondsSinceCollection = Math.floor((now - lastCollection) / 1000);
        const efficiency = miningData.efficiency || 1.0;
        
        // Calculate rewards based on time since collection (zero if mining disabled or cap reached)
        // Count only mining parts, not all parts
        const miningParts = user?.miningRigInventory || [];
        const partsCount = miningParts.length;
        const currentHashRate = BASE_HASH_RATE + (partsCount * HASH_RATE_PER_PART);
        const currentMiningRate = currentHashRate * COINS_PER_HASH_RATE;
        const calculatedRewards = (miningData.miningDisabled || miningData.capReached) ? 0 : parseFloat((secondsSinceCollection * currentMiningRate).toFixed(3));
        
        // Mining calculation completed (logging reduced)
        
        // Check if rig was recently rebooted (within last 5 minutes)
        const lastReboot = miningData.lastReboot || now;
        const recentlyRebooted = (now - lastReboot) < 5 * 60 * 1000; // 5 minutes
        
        // Determine status based on server data - always trust server unless mining disabled
        let isActive, status;
        
        if (miningData.capReached) {
          // Mining cap reached - rig is offline
          isActive = false;
          status = 'Offline - Mining Cap Reached';
          console.log('Mining cap reached - rig is offline');
        } else if (miningData.miningDisabled) {
          // Always respect server's disabled status - cannot be overridden
          isActive = false;
          status = 'Locked - Tasks Complete';
          console.log('Mining disabled due to expert tasks completion');
        } else {
          // For normal users, always show as running since mining is continuous
          isActive = true;
          status = 'Running';
        }
        
        // Server mining status retrieved
        
        // Update mining status
        setMiningStatus({
          ...miningData,
          isActive,
          status,
          pendingRewards: calculatedRewards,
          lastUpdateTime: now
        });
        
        // Update user context with latest efficiency (skip sync to prevent cascading updates)
        if (user && user.miningRig && miningData.efficiency && user.miningRig.efficiency !== miningData.efficiency) {
          console.log(`Updating user mining efficiency: ${user.miningRig.efficiency} -> ${miningData.efficiency}`);
          updateUser({
            miningRig: {
              ...user.miningRig,
              efficiency: miningData.efficiency
            }
          }, true); // Skip sync to prevent refresh loops
        }
        
        // We're no longer updating localStorage - only server matters
        // Using server mining status
      }
    } catch (err) {
      console.log('Could not fetch mining status:', err);
      
      // If fetch fails, maintain the current UI state rather than changing it
      // This prevents the rig from going offline due to a network error
      setMiningStatus(prevStatus => {
        if (!prevStatus) return null;
        return { ...prevStatus, lastUpdateTime: Date.now() };
      });
    }
  }, [user?.username, user?.miningRig?.efficiency]);
  
  // Store the latest fetchMiningStatus function in ref
  fetchMiningStatusRef.current = fetchMiningStatus;
  
  // Handle collecting mining rewards
  const handleCollectRewards = async () => {
    if (!miningStatus || collectingRewards || miningStatus.pendingRewards <= 0) return;
    
    setCollectingRewards(true);
    try {
      // Get the pending rewards - save this value for the API call
      const pendingAmount = parseFloat(miningStatus.pendingRewards);
      
      console.log(`Collecting ${pendingAmount} WR Coins`);
      
      // Store current isActive status before updating UI
      const currentIsActive = miningStatus.isActive !== undefined ? miningStatus.isActive : true;
      const currentStatus = miningStatus.status || (currentIsActive ? 'Running' : 'Offline');
      
      // Get current time
      const currentTime = Date.now();
      
      // Store the current balance for reference
      const currentCoins = user?.wrCoins || 0;
      console.log(`Current balance before collection: ${currentCoins} WR coins`);
      
      // Immediately update UI to show 0 balance but PRESERVE isActive status
      setMiningStatus(prevStatus => ({
        ...prevStatus,
        pendingRewards: 0,
        lastCollection: currentTime,
        isActive: currentIsActive,
        status: currentStatus
      }));
      
      // IMPORTANT: Record collection in both session and local storage BEFORE API call
      recordCollection(pendingAmount);
      
      // Call API to collect rewards with the explicit pendingAmount parameter and current time
      // This ensures the server gets both the correct amount and timestamp
      console.log(`Sending collection request to server with pendingRewards=${pendingAmount} and lastCollection=${new Date(currentTime).toISOString()}`);
      const result = await collectMiningCoins(pendingAmount, currentTime);
      
      console.log('Collection result from server:', result);
      
      // Check if the server response has explicit isActive status information
      const serverIsActive = result && result.isActive !== undefined ? result.isActive : currentIsActive;
      const serverStatus = result && result.status ? result.status : (serverIsActive ? 'Running' : 'Offline');
      const serverLastCollection = result && result.lastCollection ? result.lastCollection : currentTime;
      const serverCapReached = result && result.capReached !== undefined ? result.capReached : false;
      const serverTotalMined = result && result.totalMined !== undefined ? result.totalMined : 0;
      const serverMiningCap = result && result.miningCap !== undefined ? result.miningCap : 50000000;
      
      // Update UI with the server's status info
      setMiningStatus(prevStatus => ({
        ...prevStatus,
        isActive: serverIsActive && !serverCapReached,
        status: serverCapReached ? 'Offline - Mining Cap Reached' : serverStatus,
        lastCollection: serverLastCollection,
        pendingRewards: 0, // Ensure rewards are reset
        capReached: serverCapReached,
        totalMined: serverTotalMined,
        miningCap: serverMiningCap
      }));
      
      console.log(`Updated collection timestamp to: ${new Date(serverLastCollection).toISOString()}`);
      
      // Make sure session storage is updated with the server timestamp
      try {
        sessionStorage.setItem(LAST_COLLECTION_KEY, serverLastCollection.toString());
        console.log(`Updated session storage with server timestamp: ${new Date(serverLastCollection).toISOString()}`);
      } catch (storageError) {
        console.error('Error updating session storage:', storageError);
      }
      
      // Update user balance in context with the server-returned totalCoins value if available
      if (user) {
        // Use the server-provided total coins value if available, otherwise calculate locally
        const newBalance = result && result.totalCoins !== undefined 
          ? result.totalCoins 
          : (user.wrCoins || 0) + pendingAmount;
        
        console.log(`Updating user balance: ${user.wrCoins} → ${newBalance} (server total: ${result?.totalCoins})`);
        
        // Update user data in context which will trigger UI refresh
        updateUser({
          wrCoins: newBalance,
          miningRig: {
            ...user.miningRig,
            lastCollection: serverLastCollection,
            isActive: serverIsActive,
            status: serverStatus,
            totalMined: result?.totalMined || user.miningRig?.totalMined || 0,
            miningCap: result?.miningCap || user.miningRig?.miningCap || 50000000
          }
        });
        
        // Add notification for mining reward collection
        notifyMiningReward(pendingAmount, miningStatus.efficiency || 1.0);
        
        // Force a refresh of user data from server after a short delay
        setTimeout(async () => {
          try {
            console.log('Forcing sync of user data with server...');
            await syncUserData({});
            console.log('User data synced with server after collection');
          } catch (syncError) {
            console.error('Error syncing user data:', syncError);
          }
        }, 1000);
      }
      
      // Fetch fresh mining status data after collection - increase the delay to ensure DB is updated
      setTimeout(() => {
        console.log('Fetching fresh mining status after collection');
        fetchMiningStatus();
      }, 3000); // Increased from 1500ms to 3000ms
      
      // Schedule another refresh after a longer delay to ensure we get the correct state
      setTimeout(() => {
        console.log('Secondary refresh of mining status to ensure consistency');
        fetchMiningStatus();
      }, 10000); // 10 seconds later
      
    } catch (err) {
      console.error('Error collecting mining rewards:', err);
    } finally {
      setCollectingRewards(false);
    }
  };
  
  // Handle manual user data refresh
  const handleRefreshData = async () => {
    if (refreshing) return;
    
    console.log("Manual refresh button clicked, refreshing user data...");
    setRefreshing(true);
    
    try {
      // Get fresh user data to ensure we have latest inventory and stats
      console.log("Getting fresh user profile data...");
      const freshUserData = await getUserProfile();
      console.log('Fresh user data received:', {
        wrCoins: freshUserData.wrCoins,
        partsCount: freshUserData.miningRigInventory?.length || 0,
        miningEfficiency: freshUserData.miningRig?.efficiency || 1.0
      });
      
      // Also refresh mining status directly from API
      const miningData = await getMiningStatus();
      console.log('Fresh mining status received:', {
        efficiency: miningData.efficiency,
        partsCount: miningData.partsCount
      });
      
      // Update user context with fresh data
      console.log("Updating user context with fresh data...");
      updateUser({
        ...freshUserData,
        // Preserve the token which is not included in the profile response
        token: user.token,
        // Update mining rig info
        miningRig: {
          ...(freshUserData.miningRig || {}),
          efficiency: miningData.efficiency || freshUserData.miningRig?.efficiency || 1.0
        }
      });
      
      // Update mining status state with new data
      setMiningStatus(prevStatus => ({
        ...prevStatus,
        ...miningData,
        lastUpdateTime: Date.now()
      }));
      
      // Call syncUserData as well to ensure everything is in sync
      await syncUserData({});
      
      console.log("User data refreshed successfully");
      
    } catch (err) {
      console.error('Error refreshing user data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  // Handle resetting the mining balance
  const handleRebootRig = async () => {
    if (rebootingRig) return;
    
    console.log("Reset balance button clicked, starting reset process...");
    setRebootingRig(true);
    
    // No need to update localStorage anymore
    console.log("Starting balance reset process - will get status from server after reset");
    
    // Immediately update UI to show balance is being reset
    setMiningStatus(prevStatus => ({
      ...prevStatus,
      status: 'Resetting...',
      pendingRewards: 0
    }));
    
    try {
      // Set reset timestamp
      const resetTime = Date.now();
      
      // Call the API to reset the mining balance
      console.log("Calling balance reset API...");
      const result = await rebootMiningRig();
      console.log("Reset API result:", result);
      
      // IMPORTANT: Force a proper mining status update
      // This needs to happen without going through the server again
      setMiningStatus(prevStatus => {
        // Create a complete new status object with fresh collection time
        const newStatus = {
          ...(prevStatus || {}),
          status: 'Running',
          lastCollection: resetTime,
          pendingRewards: 0
        };
        console.log("Forced mining status update:", newStatus);
        return newStatus;
      });
      
      // Update the user's mining info with new collection time
      if (user) {
        console.log("Updating user context with new collection time");
        updateUser({
          miningRig: {
            ...user.miningRig,
            lastCollection: resetTime
          }
        });
      }
      
      // Temporarily disable automatic status refresh
      // This prevents premature fetching before the server updates
      sessionStorage.setItem('disable_mining_refresh', (Date.now() + 30000).toString());
      console.log("Disabled automatic mining status refresh for 30 seconds");
      
      // Schedule one fetch in a few seconds to get the updated state from server
      setTimeout(() => {
        console.log("Fetching updated status from server after reboot");
        fetchMiningStatus();
      }, 5000);
      
      // Force update mining status one more time after a delay
      // This ensures the UI shows the correct status
      setTimeout(() => {
        setMiningStatus(prevStatus => ({
          ...(prevStatus || {}),
          status: 'Running',
          lastCollection: resetTime,
          pendingRewards: 0
        }));
        console.log("Mining status update after timeout:", { status: 'Running', pendingRewards: 0 });
      }, 3000);
      
    } catch (err) {
      console.error('Error resetting mining balance:', err);
      
      // Even in case of error, update UI to show fresh collection time
      console.log("Error occurred, but resetting mining balance UI anyway");
      setMiningStatus(prevStatus => ({
        ...prevStatus,
        status: 'Running',
        lastCollection: Date.now(),
        pendingRewards: 0
      }));
      
    } finally {
      // Small delay before completing to ensure UI updates are visible
      setTimeout(() => {
        setRebootingRig(false);
        console.log("Reset process completed");
        
        // Final force update to ensure UI shows proper status
        setMiningStatus(prevStatus => ({
          ...(prevStatus || {}),
          status: 'Running',
          pendingRewards: 0
        }));
      }, 2000);
    }
  };
  
  // Initialize mining status and set up refresh intervals
  useEffect(() => {
    // Get default values
    const now = Date.now();
    
    // Show loading state while we fetch from server
    setLoading(true);
    
    // Start with an empty mining rig state - will be populated from server only
    const defaultMiningStatus = {
      hashrate: 24.5,
      pendingRewards: 0,
      rigType: 'Basic',
      efficiency: 1.0,
      status: 'Initializing...',
      lastCollection: now - 3600000,
      lastUpdateTime: now
    };
    
    // Set initial status
    setMiningStatus(defaultMiningStatus);
    setLoading(false);
    
    // Fetch initial mining status
    if (fetchMiningStatusRef.current) {
      fetchMiningStatusRef.current();
    }
    
    // Server refresh interval (every 10 seconds)
    const serverRefreshInterval = setInterval(() => {
      if (fetchMiningStatusRef.current) {
        fetchMiningStatusRef.current();
      }
    }, 10000);
    
    // Client-side update interval (every second)
    const clientUpdateInterval = setInterval(() => {
      // Update client time
      setClientTime(Date.now());
      
      // We no longer use localStorage for mining status
      // Always use the current state in memory, which is refreshed from server
      const currentStoredStatus = {
        isActive: miningStatus?.isActive || false, 
        status: miningStatus?.status || 'Offline'
      };
      
      // Update mining balance - now based entirely on lastCollection timestamp
      setMiningStatus(prevStatus => {
        if (!prevStatus) return prevStatus;
        
        // First check if we need to update status to match localStorage
        const needsStatusUpdate = prevStatus && (
          prevStatus.isActive !== currentStoredStatus.isActive || 
          prevStatus.status !== currentStoredStatus.status
        );
        
        // Only log if there's an actual inconsistency that needs fixing
        if (needsStatusUpdate) {
          // Removed excessive logging - only log critical inconsistencies
        }
        
        // Check if mining is disabled or rig is offline - if so, don't accumulate rewards
        if (prevStatus && prevStatus.miningDisabled) {
          // If mining is disabled due to expert tasks completion, maintain locked status
          return {
            ...prevStatus,
            isActive: false,
            status: 'Locked - Tasks Complete',
            pendingRewards: 0,
            lastUpdateTime: Date.now()
          };
        } else if (!currentStoredStatus.isActive && currentStoredStatus.miningDisabled) {
          // If rig is offline due to mining disabled, maintain locked status
          return {
            ...prevStatus,
            isActive: false,
            status: 'Locked - Tasks Complete',
            lastUpdateTime: Date.now()
          };
        }
        
        // Always update status based on miningDisabled state
        const updatedStatus = {
          ...prevStatus,
          isActive: !prevStatus.miningDisabled,
          status: prevStatus.miningDisabled ? 'Locked - Tasks Complete' : 'Running',
          miningDisabled: prevStatus.miningDisabled
        };
        
        // Prevent updates if mining is disabled
        if (updatedStatus.miningDisabled) {
          return {
            ...updatedStatus,
            isActive: false,
            status: 'Locked - Tasks Complete',
            pendingRewards: 0,
            lastUpdateTime: Date.now()
          };
        }
        
        // Check for recent collection
        if (hasRecentCollection()) {
          return {
            ...updatedStatus,
            pendingRewards: 0,
            lastUpdateTime: Date.now()
          };
        }
        
        // Get the current time
        const now = Date.now();
        
        // Calculate rewards based on time since last collection
        const lastCollection = prevStatus.lastCollection || now;
        const secondsSinceCollection = (now - lastCollection) / 1000;
        
        // Only add rewards if the rig is active and mining is not disabled and cap not reached
        if (updatedStatus.isActive && updatedStatus.status !== 'Offline' && !updatedStatus.miningDisabled && !updatedStatus.capReached) {
          // Calculate total rewards based on time since last collection
          // Count only mining parts, not all parts
          const miningParts = user?.miningRigInventory || [];
          const partsCount = miningParts.length;
          const currentHashRate = BASE_HASH_RATE + (partsCount * HASH_RATE_PER_PART);
          const currentMiningRate = currentHashRate * COINS_PER_HASH_RATE;
          const totalRewards = currentMiningRate * secondsSinceCollection;
          
          // Removed excessive mining calculation logging
          
          // Return updated status with new rewards
          return {
            ...updatedStatus,
            pendingRewards: parseFloat(totalRewards.toFixed(3)),
            lastUpdateTime: now
          };
        }
        
        // If rig is not active, just update timestamp without adding rewards
        return {
          ...updatedStatus,
          lastUpdateTime: now
        };
      });
    }, 1000);
    
    // Clean up on unmount
    return () => {
      clearInterval(serverRefreshInterval);
      clearInterval(clientUpdateInterval);
    };
  }, []); // Remove fetchMiningStatus dependency to prevent infinite loops


  // Show loading state if needed
  if (loading && !miningStatus) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard">
      <h1>Welcome, {user?.username || 'User'}</h1>
      
      {/* Premium Subscription Status */}
      {(user?.subscriptions?.doubleXP?.active || user?.subscriptions?.doubleMining?.active || user?.subscriptions?.comboMonthly?.active) && (
        <div className="premium-status-banner">
          <div className="premium-header">
            <span className="premium-icon">💎</span>
            <h3>Premium Benefits Active</h3>
            <span className="premium-status">🟢 SUBSCRIBED</span>
          </div>
          <div className="premium-benefits">
            {user?.subscriptions?.doubleXP?.active && (
              <div className="benefit-item">
                <span className="benefit-icon">✨</span>
                <span>Double XP from all activities</span>
              </div>
            )}
            {user?.subscriptions?.doubleMining?.active && (
              <div className="benefit-item">
                <span className="benefit-icon">⛏️</span>
                <span>Double mining collection rates</span>
              </div>
            )}
            {user?.subscriptions?.comboMonthly?.active && (
              <>
                <div className="benefit-item">
                  <span className="benefit-icon">✨</span>
                  <span>Double XP from all activities</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">⛏️</span>
                  <span>Double mining collection rates</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      
      <div className="dashboard-header">
        <div className="dashboard-stats">
          <div className="stat-card">
            <h3>Level</h3>
            <p className="stat-value">{Math.floor((user?.xp || 0) / 1000) + 1}</p>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${((user?.xp || 0) % 1000) / 10}%` }} 
              ></div>
            </div>
            <p className="progress-text">Progress to next level: {(user?.xp || 0) % 1000}/{1000}</p>
          </div>
          <div className="stat-card">
            <h3>Experience</h3>
            <p className="stat-value">{user?.xp || 0} XP</p>
            <p className="stat-subtitle">
              Complete tasks to earn more XP
              {(user?.subscriptions?.doubleXP?.active || user?.subscriptions?.comboMonthly?.active) && 
                <span className="premium-boost"> (2x Premium Boost Active!)</span>
              }
            </p>
          </div>
          <div className="stat-card">
            <h3>WR Coins</h3>
            <p className="stat-value">{parseFloat(user?.wrCoins || 0).toFixed(3)}</p>
            <p className="stat-subtitle">
              Used to purchase computer upgrades
              {(user?.subscriptions?.doubleMining?.active || user?.subscriptions?.comboMonthly?.active) && 
                <span className="premium-boost"> (2x Mining Boost Active!)</span>
              }
            </p>
          </div>
        </div>
      </div>
      
      {miningStatus && (
        <div className="mining-status">
          <h2>Mining Rig Status</h2>
          <div className={`status-indicator ${miningStatus.miningDisabled || miningStatus.capReached ? 'locked' : 'active'}`}>
            {miningStatus.capReached ? 'Offline - Mining Cap Reached' : 
             miningStatus.miningDisabled ? 'Locked - Tasks Complete' : 
             (miningStatus.status || 'Always Online')}
          </div>
          
          <div className="mining-rig-visual">
            <div className="rig-icon">💻</div>
            
            {/* Mining rig boost meter */}
            <div className="boost-meter">
              <div className="boost-label">Mining Boost</div>
              <div className="boost-bar">
                <div 
                  className="boost-fill" 
                  style={{ width: `${Math.min(Math.round((miningStatus.efficiency - 1) * 100), 100)}%` }}
                ></div>
                <span className="boost-percentage">+{Math.round((miningStatus.efficiency - 1) * 100)}%</span>
              </div>
            </div>
            
          </div>
          
          <div className="mining-details">
            <div className="mining-stat highlight">
              <span className="label">Mining Balance:</span>
              <span className="value coins">{formatNumber(hasRecentCollection() ? 0 : (miningStatus.pendingRewards || 0))}</span>
            </div>
            <div className="mining-stat">
              <span className="label">Mining rate:</span>
              <span className="value">{(() => {
                // Count only mining parts, not all parts
                const miningParts = user?.miningRigInventory || [];
                const partsCount = miningParts.length;
                const currentHashRate = BASE_HASH_RATE + (partsCount * HASH_RATE_PER_PART);
                const currentRate = currentHashRate * COINS_PER_HASH_RATE;
                return currentRate.toFixed(6);
              })()} WR Coins/sec</span>
            </div>
            <div className="mining-stat">
              <span className="label">Mining Progress:</span>
              <span className="value">{(() => {
                // Count only mining parts, not all parts
                const miningParts = user?.miningRigInventory || [];
                const partsCount = miningParts.length;
                const currentHashRate = BASE_HASH_RATE + (partsCount * HASH_RATE_PER_PART);
                const currentRate = currentHashRate * COINS_PER_HASH_RATE;
                const percentage = (currentRate / 0.000400 * 100).toFixed(0);
                return `${percentage}% (${partsCount}/10 parts)`;
              })()}</span>
            </div>
            <div className="mining-stat">
              <span className="label">Hash Rate:</span>
              <span className="value">{(() => {
                // Count only mining parts, not all parts
                const miningParts = user?.miningRigInventory || [];
                const partsCount = miningParts.length;
                return (BASE_HASH_RATE + (partsCount * HASH_RATE_PER_PART)).toFixed(0);
              })()} H/s</span>
            </div>
            {miningStatus.totalMined !== undefined && (
              <div className="mining-stat">
                <span className="label">Total Mined:</span>
                <span className="value">
                  {miningStatus.totalMined < 1000 
                    ? `${miningStatus.totalMined.toFixed(3)} / ${(miningStatus.miningCap / 1000000).toFixed(0)}M WR`
                    : `${(miningStatus.totalMined / 1000000).toFixed(2)} / ${(miningStatus.miningCap / 1000000).toFixed(0)}M`
                  }
                </span>
              </div>
            )}
          </div>
          
          <div className="mining-actions">
            <button 
              className={`action-button ${miningStatus.pendingRewards <= 0 || hasRecentCollection() || miningStatus.miningDisabled || miningStatus.capReached ? 'disabled' : ''}`}
              onClick={handleCollectRewards}
              disabled={collectingRewards || miningStatus.pendingRewards <= 0 || hasRecentCollection() || miningStatus.miningDisabled || miningStatus.capReached}
            >
              {miningStatus.capReached ? 'Cap Reached' : miningStatus.miningDisabled ? 'Mining Disabled' : (collectingRewards ? 'Collecting...' : 'Collect')}
            </button>
          </div>
        </div>
      )}
      
      <div className="tasks-cta">
        <h2>Fancy Creating a Community Challenge?</h2>
        <p>Create and share your own challenges for others to solve!</p>
        <button className="start-tasks-button" onClick={() => navigate('/system')}>
          Go to My Tasks
        </button>
      </div>
    </div>
  );
};

export default Dashboard;